import { Injectable, computed, signal } from '@angular/core';

/**
 * Global Loading Service.
 * Tracks active HTTP requests (via ApiClient onRequestStart/onRequestEnd)
 * and active router navigations (via Router events).
 * Exposes a reactive `isLoading` signal consumed by global UI components.
 */
@Injectable({
  providedIn: 'root',
})
export class LoadingService {
  private readonly activeRequests = signal<number>(0);
  private readonly routerNavigating = signal<boolean>(false);

  /** True whenever there is at least one active API request or an active route navigation. */
  readonly isLoading = computed(
    () => this.activeRequests() > 0 || this.routerNavigating(),
  );

  /** Increments the active API request counter. */
  startRequest(): void {
    this.activeRequests.update((count) => count + 1);
  }

  /** Decrements the active API request counter (floored at 0). */
  endRequest(): void {
    this.activeRequests.update((count) => Math.max(0, count - 1));
  }

  /** Sets whether router navigation is underway. */
  setNavigating(isNavigating: boolean): void {
    this.routerNavigating.set(isNavigating);
  }

  /** Resets state (useful in test suites or unexpected state recovery). */
  reset(): void {
    this.activeRequests.set(0);
    this.routerNavigating.set(false);
  }
}
